import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { onUserBankStatusUpdate, onUserDetail, onUserKycAccept, onUserKycReject } from "../../controller/UserController";
import 'react-toastify/dist/ReactToastify.css';
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, Table, Tab, Tabs, Badge, Carousel, Modal, Pagination } from 'react-bootstrap';
import {
    URL_BACKEND_ROOT
  } from "../../app/AppConst";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AccountDetailEntity } from "../../dto/account/AccountDetailEntity";
import { KycCaseEntity } from "../../dto/kyc/KycCaseEntity";
import { AccountBankEntity } from "../../dto/account/AccountBankEntity";
import { WalletTxnSearch } from "../../dto/wallet/WalletTxnSearch";
import { onWalletTxnSearch } from "../../controller/WalletController";
import { WalletTxnEntity } from "../../dto/wallet/WalletTxnEntity";
import { PageableBean } from "../../dto/pageable/PageableBean";

type userParams = {
    phoneNo: string;
};

const UserDetail = () => {
    const { phoneNo } = useParams<userParams>();
    const [activeKey, setActiveKey] = useState<string>('kyc-info'); // control selected Tab from Tabs
    const [selectedKyc, setSelectedKyc] = useState<KycCaseEntity>({});  // selected KYC details showing on screen
    const [activeIndex, setActiveIndex] = useState(0);
    const [accountDetail, setAccountDetail] = useState<AccountDetailEntity>({});
    const [showRejectModal, setShowRejectModal] = useState(false);

    const [kycCaseAction, setKycCaseAction] = useState<KycCaseEntity>({});

    const navigate = useNavigate();

    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        } else {
            userDetailGet();
        }
    }, []);

    const userDetailGet = async () => {
        const response = await onUserDetail(phoneNo!);
        const data = response.data;
        if (data !== undefined) {
            console.log(data);
            setAccountDetail({
                account: data.account,
                individualProfile: data.individualProfile,
                companyProfile: data.companyProfile,
                kycCases: data.kycCases,
                addresses: data.addresses,
                banks: data.banks,
                wallet: data.wallet,
                transactions: data.transactions
            });
        }
    }

    const kycDetail = (kyc: KycCaseEntity) => {
        setSelectedKyc(kyc);
    }

    const kycAccept = async (kycId: number) => {
        const response = await onUserKycAccept(kycId);
        if (response && response.status === 0) {
            toast.success(response.msg);
        } else {
            toast.error(response.msg);
        }
        userDetailGet();
    }

    const kycReject = async () => {
        const response = await onUserKycReject(kycCaseAction);
        if (response && response.status === 0) {
            toast.success(response.msg);
        } else {
            toast.error(response.msg);
        }
        userDetailGet();
        setShowRejectModal(false);
    }

    const openRejectReasonModal = (kycId: number) => {
        setKycCaseAction({
            ...kycCaseAction,
            id: kycId
        })
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
    };

    const getStatusVariant = (status?: string) => {
        switch (status) {
            case "APPROVED":
            case "ACCEPTED":
                return "success";
            case "REJECTED":
                return "danger";
            case "PENDING":
            case "SUBMITTED":
                return "warning";
            case "EXPIRED":
                return "info";
            default:
                return "secondary";
        }
    };

    const bankStatusChange = async (accountBankId: number, status: string) => {
        const payLoad : AccountBankEntity = {
            id: accountBankId,
            status: status
          };

        const response = await onUserBankStatusUpdate(payLoad);

        if (response && response.status === 0) {
            toast.success(response.msg || "Bank status updated");
        } else {
            toast.error(response.msg || "Failed to update bank status");
        }
    
        userDetailGet(); // refresh data
    };

    const [walletTxnSearch, setWalletTxnSearch] = useState<WalletTxnSearch>({
        limit: 10
    });

    // ---- Pagination click
    const changePage = (page: number) => {
        if (!accountDetail.transactions) return;
        if (page < 0 || page >= accountDetail.transactions?.totalPages!) return;
        load(page);
    };

    const load = async (page: number = 0) => {
        const params: WalletTxnSearch = {
            ...walletTxnSearch,
            walletId: accountDetail.wallet?.id,
            offset: page
        };

        const response = await onWalletTxnSearch(params);

        if (response.status !== 0) {
            toast.error("Wallet transaction read error.");
            return;
        }

        const pageable = response.data;

        const beans: Array<WalletTxnEntity> = pageable.beans;

        const searchParam: PageableBean<WalletTxnEntity> = {
            totalPages: response.data.totalPages,
            totalElements: response.data.totalElements,
            offset: page,
            beans: beans
        }

        setAccountDetail(prev => ({
            ...prev,
            transactions: searchParam
        }));

        setWalletTxnSearch(prev => ({ ...prev, offset: page }));
    };

    return (
        <div className="pl-30 pr-30">
            <div><Toaster position="top-right" reverseOrder={false}/></div>
            <Container fluid>
                <h3 className="mb-30"><a onClick={() => navigate(-1)}><big>&#8592;</big></a>Account Detail</h3>
            </Container>
            <Container fluid>
                <Row className="d-flex">
                    <Col md={3} lg={3} xs={3}>
                        <div className="mb-30">
                            <Card>
                                <Card.Header>
                                    Profile
                                </Card.Header>
                                <Card.Body>
                                    <div className="thumbnail-wrapper">
                                        <a href={`${URL_BACKEND_ROOT}/v1/file/api/files/${accountDetail.account?.avatarFileUuid}`}
                                        target="_blank" 
                                        style={{ width: "100%", height: "100%", textAlign: "center" }}>
                                            <img src={`${URL_BACKEND_ROOT}/v1/file/d/${accountDetail.account?.avatarFileUuid}`}
                                            alt="profile-icon" 
                                            style={{ maxWidth: "100%", maxHeight: "100%" }} />
                                        </a>
                                    </div>
                                </Card.Body>
                                <Card.Footer>
                                <Table hover>
                                    <tr>
                                        <th>Lastname</th>
                                        <td>{accountDetail.individualProfile?.lastName}</td>
                                    </tr>
                                    <tr>
                                        <th>Firstname</th>
                                        <td>{accountDetail.individualProfile?.firstName}</td>
                                    </tr>
                                    <tr>
                                        <th>Register No</th>
                                        <td>{accountDetail.individualProfile?.regNo}</td>
                                    </tr>
                                    <tr>
                                        <th>Phone No</th>
                                        <td>{accountDetail.account?.phoneNo}</td>
                                    </tr>
                                    <tr>
                                        <th>KYC Level</th>
                                        <td>{accountDetail.account?.kycLevel}</td>
                                    </tr>
                                    <tr>
                                        <th>KYC Status</th>
                                        <td>
                                            <Badge bg={getStatusVariant(accountDetail.account?.kycStatus)}>
                                                {accountDetail.account?.kycStatus}
                                            </Badge> 
                                        </td>
                                    </tr>
                                </Table>
                                </Card.Footer>
                            </Card>
                        </div>
                    </Col>
                    <Col md={9} lg={9} xs={9}>
                        <Tabs
                            defaultActiveKey="account-info"
                            id="account-info-tab"
                            className="mb-3"
                            activeKey={activeKey}
                            onSelect={(k) => k && setActiveKey(k)}
                            >
                            <Tab eventKey="kyc-info" title="KYC">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Created At</th>
                                            <th>Submitted at</th>
                                            <th>Reviewed at</th>
                                            <th>Type</th>
                                            <th>Level</th>
                                            <th>Status</th>
                                            <th>Rejected</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accountDetail.kycCases &&
                                            accountDetail.kycCases.map((kyc, kycIdx) => {
                                                return (
                                                    <tr key={kycIdx}>
                                                        <td>
                                                            <Button variant="secondary" size="sm" className="me-2">
                                                                <EditIcon fontSize="small"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => kycDetail(kyc)}/>
                                                            </Button>
                                                        </td>
                                                        <td>{kyc.createdAt
                                                            ? new Date(kyc.createdAt).toLocaleString()
                                                            : ''}</td>
                                                        <td>{kyc.submittedAt
                                                            ? new Date(kyc.submittedAt).toLocaleString()
                                                            : ''}</td>
                                                        <td>{kyc.reviewedAt
                                                            ? new Date(kyc.reviewedAt).toLocaleString()
                                                            : ''}</td>
                                                        <td>{kyc.accountType}</td>
                                                        <td>{kyc.level}</td>
                                                        <td><Badge bg={getStatusVariant(kyc.status)}>
                                                            {kyc.status}
                                                        </Badge></td>
                                                        <td>{kyc.rejectedReason}</td>
                                                        <td>
                                                            <Button variant="success" size="sm" className="me-2" 
                                                            onClick={() => kycAccept(kyc.id!)} >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </Button>
                                                            <Button variant="danger" size="sm" className="me-2" 
                                                            onClick={() => openRejectReasonModal(kyc.id!)} >
                                                                <CancelIcon fontSize="small" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>
                                </Table>
                            </Tab>
                            <Tab eventKey="user-bank" title="Bank Accounts">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>Account No</th>
                                            <th>Account Name</th>
                                            <th>Bank RTGS</th>
                                            <th>Bank Name</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accountDetail.banks &&
                                            accountDetail.banks.map((bank, bankIdx) => {
                                                return (
                                                    <tr key={bankIdx}>
                                                        <td>{bank.accountNo}</td>
                                                        <td>{bank.accountName}</td>
                                                        <td>{bank.bank?.rtgs}</td>
                                                        <td>{bank.bank?.name}</td>
                                                        {/* <td>{bank.status}</td> */}
                                                        <td>
                                                        <Form.Select
                                                            size="sm"
                                                            value={bank.status}
                                                            onChange={(e) => bankStatusChange(bank.id!, e.target.value)}
                                                        >
                                                            <option value="SUBMITTED">SUBMITTED</option>
                                                            <option value="REVIEWED">REVIEWED</option>
                                                            <option value="ACTIVE">ACTIVE</option>
                                                            <option value="DELETED">DELETED</option>
                                                        </Form.Select>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>
                                </Table>
                            </Tab>
                            <Tab eventKey="user-address" title="Address">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>City</th>
                                            <th>District</th>
                                            <th>Avenue</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accountDetail.addresses &&
                                            accountDetail.addresses.map((address, addressIdx) => {
                                                return (
                                                    <tr key={addressIdx}>
                                                        <td>{address.city}</td>
                                                        <td>{address.district}</td>
                                                        <td>{address.avenue}</td>
                                                        <td>{address.description}</td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>
                                </Table>
                            </Tab>
                            <Tab eventKey="user-wallet" title="Wallet">
                                <Row className="mt-3">
                                    {/* BALANCE CARD */}
                                    <Col md={4}>
                                        <Card className="mb-3 shadow-sm">
                                            <Card.Body>
                                            <h6 className="text-muted">Total Balance</h6>
                                            <h2 style={{ fontWeight: 700 }}>
                                                {accountDetail.wallet?.currency || "MNT"} {accountDetail.wallet?.balance?.toLocaleString() || "0"}
                                            </h2>

                                            <div className="d-flex justify-content-between mt-3">
                                                <div>
                                                <small className="text-muted">Available</small>
                                                <div>
                                                    {accountDetail.wallet?.currency || "MNT"} {accountDetail.wallet?.balance?.toLocaleString() || "0"}
                                                </div>
                                                </div>
                                                <div>
                                                <small className="text-muted">Hold</small>
                                                <div className="text-danger">
                                                    {accountDetail.wallet?.currency || "MNT"} {accountDetail.wallet?.hold?.toLocaleString() || "0"}
                                                </div>
                                                </div>
                                            </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* RECENT TRANSACTIONS */}
                                    <Col md={8}>
                                        <Card className="shadow-sm">
                                            <Card.Header>
                                                Recent Transactions
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                            <Table hover responsive className="mb-0">
                                                <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Description</th>
                                                    <th>In/Out</th>
                                                    <th>Type</th>
                                                    <th>Amount</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {(accountDetail.transactions?.beans ?? []).length > 0 ? (
                                                    accountDetail.transactions!.beans.map((tx, idx) => (
                                                    <tr key={idx}>
                                                        <td>{tx.createdAt
                                                            ? new Date(tx.createdAt).toLocaleString()
                                                            : "-"}</td>
                                                        <td>{tx.description}</td>
                                                        <td>{tx.direction}</td>
                                                        <td>{tx.txnType}</td>
                                                        <td>{tx.amount}</td>
                                                    </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={5} className="text-center text-muted py-3">
                                                            No records.
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                            </Table>
                                            </Card.Body>
                                        </Card>
                                        {accountDetail.transactions && accountDetail.transactions.totalPages > 1 && (() => {
                                            const page = accountDetail.transactions;
                                            const current = page.offset ?? 0;   // ✅ safety

                                            const start = Math.max(current - 2, 0);
                                            const end = Math.min(start + 5, page.totalPages);

                                            return (
                                                <Row>
                                                    <Col className="d-flex justify-content-start mt-3">
                                                        <Pagination>
                                                            <Pagination.First
                                                                disabled={current === 0}
                                                                onClick={() => changePage(0)}
                                                            />

                                                            <Pagination.Prev
                                                                disabled={current === 0}
                                                                onClick={() => changePage(current - 1)}
                                                            />

                                                            {Array.from({ length: end - start }).map((_, idx) => {
                                                                const pageNumber = start + idx;

                                                                return (
                                                                    <Pagination.Item
                                                                        key={pageNumber}
                                                                        active={pageNumber === current}
                                                                        onClick={() => changePage(pageNumber)}
                                                                    >
                                                                        {pageNumber + 1}
                                                                    </Pagination.Item>
                                                                );
                                                            })}

                                                            <Pagination.Next
                                                                disabled={current + 1 >= page.totalPages}
                                                                onClick={() => changePage(current + 1)}
                                                            />

                                                            <Pagination.Last
                                                                disabled={current + 1 >= page.totalPages}
                                                                onClick={() => changePage(page.totalPages - 1)}
                                                            />
                                                        </Pagination>
                                                    </Col>
                                                </Row>
                                            );
                                        })()}
                                    </Col>
                                </Row>
                            </Tab>
                            {/* <Tab eventKey="user-role" title="Хандах эрх">
                                Хандах эрх
                            </Tab> */}
                        </Tabs>
                    </Col>
                </Row>
            </Container>
            {/* SELECTED KYC */}
            {activeKey === "kyc-info" && selectedKyc && (
            <Container fluid className="mt-4">
                <Row>
                    {/* LEFT SIDE — DOCUMENT SLIDER */}
                    <Col md={3}>
                        <div className="mb-2">
                            <h5 style={{ fontWeight: "bold", color: "#4a6ff" }}>
                                {selectedKyc?.documents?.[activeIndex]?.docType || "Documents"}
                            </h5>
                        </div>
                        {(selectedKyc.documents ?? []).length > 0 ? (
                        <Carousel activeIndex={activeIndex} onSelect={(i) => setActiveIndex(i)}>
                            {selectedKyc.documents!.map((doc, idx) => (
                            <Carousel.Item key={idx}>
                                <img
                                src={`${URL_BACKEND_ROOT}/v1/file/api/files/${doc.fileUuid}`}
                                className="d-block w-100"
                                style={{ maxHeight: 400, objectFit: "contain" }}
                                />
                            </Carousel.Item>
                            ))}
                        </Carousel>
                        ) : (
                        <Card className="p-3 text-center">
                            No documents uploaded
                        </Card>
                        )}
                    </Col>

                    {/* RIGHT SIDE — DETAILS + FIELDS */}
                    <Col md={9}>
                        <Card>
                            <Card.Header>KYC Info</Card.Header>
                            <Card.Body>
                                <Table bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Key</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selectedKyc.fields ?? []).map((f, idx) => (
                                            <tr key={idx}>
                                                <td>{f.fieldKey}</td>
                                                <td>{f.fieldValue}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            )}

            <Modal show={showRejectModal} onHide={closeRejectModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>KYC Rejection</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Reject Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                onChange={(event) => setKycCaseAction({
                                    ...kycCaseAction,
                                    rejectedReason: event.target.value
                                })}
                                placeholder="Шалтгаан оруулна уу..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={closeRejectModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={kycReject}>
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default UserDetail; 